import { useEffect, useState } from 'react';
import { ProductType } from '../../types';
import { formatBRL } from '../../utils/format';
import { useStore } from '../../context/StoreContext';
import { useToast } from '../../context/ToastContext';
import {
  Plus,
  Search,
  Archive,
  Eye,
  X,
} from 'lucide-react';

interface AdminProductsProps {
  onNavigate: (path: string) => void;
}

export function AdminProducts({ onNavigate }: AdminProductsProps) {
  const { categories } = useStore();
  const { showToast } = useToast();
  const [products, setProducts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);

  // New product form states
  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [summary, setSummary] = useState('');
  const [descriptionHtml, setDescriptionHtml] = useState('');
  const [productType, setProductType] = useState<ProductType>('physical');
  const [basePriceReais, setBasePriceReais] = useState('180');
  const [comparePriceReais, setComparePriceReais] = useState('');
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<string[]>([]);
  const [imageUrl, setImageUrl] = useState('');
  const [imageAlt, setImageAlt] = useState('');
  const [variantTitle, setVariantTitle] = useState('Padrão');
  const [variantSku, setVariantSku] = useState('');
  const [variantStock, setVariantStock] = useState('10');
  const [submitting, setSubmitting] = useState(false);

  const fetchProducts = () => {
    setLoading(true);
    fetch('/api/admin/products')
      .then((res) => res.json())
      .then((data: any) => setProducts(data || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  const handleTitleChange = (val: string) => {
    setTitle(val);
    if (!slug || slug === title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')) {
      setSlug(val.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, ''));
      setVariantSku(`${val.slice(0, 3).toUpperCase()}-01`);
    }
  };

  const handleCreateProduct = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title || !slug || !summary) {
      showToast('error', 'Campos obrigatórios', 'Preencha título, slug e resumo da peça.');
      return;
    }

    setSubmitting(true);
    try {
      const priceCents = Math.round(parseFloat(basePriceReais.replace(',', '.')) * 100);
      const compareCents = comparePriceReais ? Math.round(parseFloat(comparePriceReais.replace(',', '.')) * 100) : undefined;
      const stock = parseInt(variantStock, 10) || 0;

      const payload = {
        title,
        slug,
        summary,
        description_html: descriptionHtml ? `<p>${descriptionHtml}</p>` : `<p>${summary}</p>`,
        product_type: productType,
        base_price_cents: priceCents,
        compare_at_price_cents: compareCents,
        category_ids: selectedCategoryIds,
        variants: [
          {
            title: variantTitle || 'Padrão',
            sku: variantSku || `${slug.toUpperCase()}-01`,
            price_cents: priceCents,
            stock_quantity: stock,
            track_inventory: productType === 'physical' ? 1 : 0,
          },
        ],
        images: imageUrl ? [{ url: imageUrl, alt_text: imageAlt || title }] : [],
      };

      const res = await fetch('/api/admin/products', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      const data = (await res.json()) as any;
      if (!res.ok) {
        throw new Error(data.error || 'Erro ao cadastrar produto.');
      }

      showToast('success', 'Produto publicado', 'A nova peça já está visível no catálogo.');
      setShowModal(false);
      resetForm();
      fetchProducts();
    } catch (err: unknown) {
      showToast('error', 'Erro', err instanceof Error ? err.message : 'Falha ao cadastrar.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleArchive = async (id: string) => {
    if (!confirm('Deseja realmente arquivar este produto? Ele deixará de aparecer no catálogo público.')) return;

    try {
      const res = await fetch(`/api/admin/products/${id}/archive`, { method: 'POST' });
      if (res.ok) {
        showToast('info', 'Produto arquivado', 'O status foi atualizado com sucesso.');
        fetchProducts();
      }
    } catch {
      showToast('error', 'Erro', 'Não foi possível arquivar o produto.');
    }
  };

  const resetForm = () => {
    setTitle('');
    setSlug('');
    setSummary('');
    setDescriptionHtml('');
    setBasePriceReais('180');
    setComparePriceReais('');
    setSelectedCategoryIds([]);
    setImageUrl('');
    setImageAlt('');
    setVariantTitle('Padrão');
    setVariantSku('');
    setVariantStock('10');
  };

  const filtered = products.filter(
    (p) =>
      p.title.toLowerCase().includes(search.toLowerCase()) ||
      p.slug.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-border-subtle pb-6">
        <div>
          <span className="text-[10px] uppercase tracking-[0.2em] font-semibold text-primary">
            Gestão do Catálogo Autoral
          </span>
          <h1 className="text-2xl sm:text-3xl font-serif font-normal text-ink mt-0.5">
            Produtos & Peças ({products.length})
          </h1>
        </div>

        <button
          onClick={() => setShowModal(true)}
          className="bg-ink text-ink-inverted px-4 py-2.5 rounded-xs text-xs uppercase tracking-wider font-medium hover:bg-primary transition-colors flex items-center gap-1.5 shadow-subtle self-start sm:self-auto"
        >
          <Plus className="w-4 h-4" /> Cadastrar Nova Peça
        </button>
      </div>

      {/* Search Bar */}
      <div className="relative max-w-md">
        <Search className="w-4 h-4 text-ink-muted absolute left-3 top-1/2 -translate-y-1/2" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Buscar produto por título ou slug..."
          className="w-full bg-surface border border-border-subtle pl-9 pr-4 py-2 text-xs rounded-xs focus:outline-none focus:border-primary"
        />
      </div>

      {/* Table */}
      <div className="bg-surface border border-border-subtle rounded-xs overflow-x-auto shadow-subtle">
        <table className="w-full text-left border-collapse text-xs">
          <thead>
            <tr className="border-b border-border-subtle bg-background-alt text-ink-muted font-medium">
              <th className="p-4">Peça</th>
              <th className="p-4">Tipo</th>
              <th className="p-4">Preço Base</th>
              <th className="p-4">Estoque Total</th>
              <th className="p-4">Status</th>
              <th className="p-4 text-right">Ações</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border-subtle">
            {loading ? (
              <tr>
                <td colSpan={6} className="p-8 text-center text-ink-muted">
                  Carregando catálogo...
                </td>
              </tr>
            ) : filtered.length === 0 ? (
              <tr>
                <td colSpan={6} className="p-8 text-center text-ink-muted italic">
                  Nenhum produto cadastrado correspondente à busca.
                </td>
              </tr>
            ) : (
              filtered.map((prod) => (
                <tr key={prod.id} className="hover:bg-background-alt/50 transition-colors">
                  <td className="p-4 flex items-center gap-3">
                    <img
                      src={prod.cover_image || 'https://images.unsplash.com/photo-1612196808214-b8e1d6145a8c?auto=format&fit=crop&w=100&q=80'}
                      alt={prod.title}
                      className="w-10 h-12 object-cover rounded-xs bg-background-alt shrink-0"
                    />
                    <div>
                      <p className="font-serif font-medium text-ink text-sm">{prod.title}</p>
                      <p className="font-mono text-[11px] text-ink-muted">{prod.slug}</p>
                    </div>
                  </td>
                  <td className="p-4">
                    <span className="capitalize px-2 py-0.5 rounded-full text-[10px] font-medium bg-surface-muted text-ink border border-border-subtle">
                      {prod.product_type}
                    </span>
                  </td>
                  <td className="p-4 font-mono font-medium text-ink">
                    {formatBRL(prod.base_price_cents)}
                  </td>
                  <td className="p-4 font-mono">
                    {prod.product_type === 'physical' ? (
                      <span className={prod.total_stock <= 3 ? 'text-amber-700 font-bold' : 'text-ink'}>
                        {prod.total_stock || 0} un.
                      </span>
                    ) : (
                      <span className="text-ink-muted">&infin; Ilimitado</span>
                    )}
                  </td>
                  <td className="p-4">
                    {prod.is_archived ? (
                      <span className="text-red-700 bg-red-50 px-2 py-0.5 rounded-full text-[10px] font-semibold">
                        Arquivado
                      </span>
                    ) : prod.is_published ? (
                      <span className="text-olive bg-olive-light px-2 py-0.5 rounded-full text-[10px] font-semibold">
                        Publicado
                      </span>
                    ) : (
                      <span className="text-ink-muted bg-surface-muted px-2 py-0.5 rounded-full text-[10px]">
                        Rascunho
                      </span>
                    )}
                  </td>
                  <td className="p-4 text-right space-x-2">
                    <button
                      onClick={() => onNavigate(`/produto/${prod.slug}`)}
                      className="p-1 text-ink-muted hover:text-ink transition-colors"
                      title="Ver no site"
                    >
                      <Eye className="w-4 h-4" />
                    </button>
                    {!prod.is_archived && (
                      <button
                        onClick={() => handleArchive(prod.id)}
                        className="p-1 text-ink-muted hover:text-red-600 transition-colors"
                        title="Arquivar produto"
                      >
                        <Archive className="w-4 h-4" />
                      </button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Modal: Create Product */}
      {showModal && (
        <div className="fixed inset-0 z-50 bg-ink/50 backdrop-blur-xs flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-surface w-full max-w-2xl rounded-xs shadow-elevated border border-border-subtle p-6 space-y-6 my-8 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between border-b border-border-subtle pb-4">
              <div>
                <h3 className="font-serif text-xl font-medium text-ink">Cadastrar Nova Peça no Catálogo</h3>
                <p className="text-xs text-ink-muted">A gravação persiste em D1 e reflete instantaneamente no storefront.</p>
              </div>
              <button onClick={() => setShowModal(false)} className="p-1 text-ink-muted hover:text-ink">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleCreateProduct} className="space-y-4 text-xs">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="sm:col-span-2">
                  <label className="font-semibold text-ink block mb-1">Título da Peça *</label>
                  <input
                    type="text"
                    required
                    value={title}
                    onChange={(e) => handleTitleChange(e.target.value)}
                    placeholder="Ex: Travessa Oval Barro Queimado"
                    className="w-full bg-background border border-border-subtle px-3 py-2 rounded-xs focus:outline-none focus:border-primary"
                  />
                </div>
                <div>
                  <label className="font-semibold text-ink block mb-1">Slug URL *</label>
                  <input
                    type="text"
                    required
                    value={slug}
                    onChange={(e) => setSlug(e.target.value)}
                    placeholder="travessa-oval-barro"
                    className="w-full bg-background border border-border-subtle px-3 py-2 rounded-xs focus:outline-none focus:border-primary font-mono text-[11px]"
                  />
                </div>
              </div>

              <div>
                <label className="font-semibold text-ink block mb-1">Resumo Curatorial *</label>
                <input
                  type="text"
                  required
                  value={summary}
                  onChange={(e) => setSummary(e.target.value)}
                  placeholder="Descrição breve que aparece no card do catálogo"
                  className="w-full bg-background border border-border-subtle px-3 py-2 rounded-xs focus:outline-none focus:border-primary"
                />
              </div>

              <div>
                <label className="font-semibold text-ink block mb-1">Descrição Completa (Texto / HTML)</label>
                <textarea
                  rows={3}
                  value={descriptionHtml}
                  onChange={(e) => setDescriptionHtml(e.target.value)}
                  placeholder="Detalhes sobre a argila, o processo do torno, temperatura de queima..."
                  className="w-full bg-background border border-border-subtle p-3 rounded-xs focus:outline-none focus:border-primary"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className="font-semibold text-ink block mb-1">Tipo de Produto *</label>
                  <select
                    value={productType}
                    onChange={(e) => setProductType(e.target.value as ProductType)}
                    className="w-full bg-background border border-border-subtle px-3 py-2 rounded-xs focus:outline-none focus:border-primary"
                  >
                    <option value="physical">Peça Física (Estoque)</option>
                    <option value="digital">Download Digital</option>
                    <option value="service">Serviço Sob Medida</option>
                  </select>
                </div>

                <div>
                  <label className="font-semibold text-ink block mb-1">Preço Base (R$) *</label>
                  <input
                    type="text"
                    required
                    value={basePriceReais}
                    onChange={(e) => setBasePriceReais(e.target.value)}
                    placeholder="180,00"
                    className="w-full bg-background border border-border-subtle px-3 py-2 rounded-xs focus:outline-none focus:border-primary font-mono"
                  />
                </div>

                <div>
                  <label className="font-semibold text-ink block mb-1">Preço Comparativo (R$)</label>
                  <input
                    type="text"
                    value={comparePriceReais}
                    onChange={(e) => setComparePriceReais(e.target.value)}
                    placeholder="Opcional"
                    className="w-full bg-background border border-border-subtle px-3 py-2 rounded-xs focus:outline-none focus:border-primary font-mono"
                  />
                </div>
              </div>

              {/* Category selector */}
              <div>
                <label className="font-semibold text-ink block mb-1">Categoria Principal</label>
                <select
                  value={selectedCategoryIds[0] || ''}
                  onChange={(e) => setSelectedCategoryIds(e.target.value ? [e.target.value] : [])}
                  className="w-full bg-background border border-border-subtle px-3 py-2 rounded-xs focus:outline-none focus:border-primary"
                >
                  <option value="">Selecione uma categoria...</option>
                  {categories.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Image & Variant initial data */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2 border-t border-border-subtle">
                <div>
                  <label className="font-semibold text-ink block mb-1">URL da Imagem da Peça</label>
                  <input
                    type="url"
                    value={imageUrl}
                    onChange={(e) => setImageUrl(e.target.value)}
                    placeholder="https://images.unsplash.com/..."
                    className="w-full bg-background border border-border-subtle px-3 py-2 rounded-xs focus:outline-none focus:border-primary"
                  />
                </div>
                <div>
                  <label className="font-semibold text-ink block mb-1">Texto Alternativo (Alt Text)</label>
                  <input
                    type="text"
                    value={imageAlt}
                    onChange={(e) => setImageAlt(e.target.value)}
                    placeholder="Descrição da imagem para acessibilidade"
                    className="w-full bg-background border border-border-subtle px-3 py-2 rounded-xs focus:outline-none focus:border-primary"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className="font-semibold text-ink block mb-1">Título da Variação</label>
                  <input
                    type="text"
                    value={variantTitle}
                    onChange={(e) => setVariantTitle(e.target.value)}
                    placeholder="Padrão / Areia / 22cm"
                    className="w-full bg-background border border-border-subtle px-3 py-2 rounded-xs focus:outline-none focus:border-primary"
                  />
                </div>
                <div>
                  <label className="font-semibold text-ink block mb-1">SKU *</label>
                  <input
                    type="text"
                    required
                    value={variantSku}
                    onChange={(e) => setVariantSku(e.target.value)}
                    placeholder="TRV-BAR-01"
                    className="w-full bg-background border border-border-subtle px-3 py-2 rounded-xs focus:outline-none focus:border-primary font-mono text-[11px]"
                  />
                </div>
                <div>
                  <label className="font-semibold text-ink block mb-1">Estoque Inicial</label>
                  <input
                    type="number"
                    min="0"
                    value={variantStock}
                    onChange={(e) => setVariantStock(e.target.value)}
                    className="w-full bg-background border border-border-subtle px-3 py-2 rounded-xs focus:outline-none focus:border-primary font-mono"
                  />
                </div>
              </div>

              <div className="pt-4 border-t border-border-subtle flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 border border-border-subtle rounded-xs hover:border-ink/40 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="bg-ink text-ink-inverted px-6 py-2 rounded-xs uppercase tracking-wider font-medium hover:bg-primary transition-colors disabled:opacity-40"
                >
                  {submitting ? 'Salvando...' : 'Publicar Peça'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
